import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, RefreshCw, Settings, Activity, AlertTriangle, ArrowUpRight } from "lucide-react";

const TIERS = [
  { key: "Low", color: "bg-ec-emerald", text: "text-ec-emerald", border: "border-ec-emerald/40", bg: "bg-ec-emerald/10" },
  { key: "Medium", color: "bg-amber-400", text: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/10" },
  { key: "High", color: "bg-orange-500", text: "text-orange-400", border: "border-orange-500/40", bg: "bg-orange-500/10" },
  { key: "Critical", color: "bg-ec-error", text: "text-ec-error", border: "border-ec-error/40", bg: "bg-ec-error/10" },
];

export default function TrustSafetyDashboard() {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/trust-safety/dashboard");
      setData(data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const scanAll = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/trust-safety/scan");
      toast.success(`Scanned ${data.scanned} accounts`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  if (!data) return <AdminLayout title="Loading…" subtitle="Trust & Safety" />;

  const totalScanned = Object.values(data.distribution).reduce((a, b) => a + b, 0) || 1;

  return (
    <AdminLayout
      subtitle="Module 8 · Trust & Safety"
      title="Risk Dashboard"
      actions={
        <div className="flex items-center gap-3">
          {data.last_scan_at && (
            <span className="text-xs font-mono-plex text-zinc-500" data-testid="ts-last-scan">
              Last scan · {new Date(data.last_scan_at).toLocaleString()}
            </span>
          )}
          <Link to="/admin/trust-safety/settings"><Button size="sm" variant="outline" className="rounded-sm border-white/10 text-white hover:bg-white/10 gap-2" data-testid="ts-settings-link"><Settings className="w-3 h-3" />Settings</Button></Link>
          <Button size="sm" onClick={scanAll} disabled={scanning} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="ts-scan-all">
            <RefreshCw className={`w-3 h-3 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Re-scan all accounts"}
          </Button>
        </div>
      }
    >
      {/* Headline alert KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 mb-8" data-testid="ts-alert-kpis">
        <AlertKpi label="Critical Alerts" value={data.alerts.critical} icon={ShieldAlert} accent="border-l-ec-error" testid="kpi-alerts-critical" />
        <AlertKpi label="High Alerts" value={data.alerts.high} icon={AlertTriangle} accent="border-l-orange-500" testid="kpi-alerts-high" />
        <AlertKpi label="Open Total" value={data.alerts.open_total} icon={Activity} accent="border-l-ec-emerald" testid="kpi-alerts-open" />
      </div>

      {/* Risk distribution bar */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02] mb-8" data-testid="ts-distribution">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow text-ec-emerald">Risk distribution</div>
              <h3 className="font-display text-xl font-bold mt-1 text-white">{totalScanned.toLocaleString()} accounts scored</h3>
            </div>
          </div>
          <div className="flex h-8 rounded-sm overflow-hidden">
            {TIERS.map((t) => {
              const pct = ((data.distribution[t.key] || 0) / totalScanned) * 100;
              return (
                <div
                  key={t.key}
                  className={`${t.color} flex items-center justify-center text-xs font-bold text-ec-ink relative group transition-all`}
                  style={{ width: `${Math.max(pct, 1)}%` }}
                  title={`${t.key}: ${data.distribution[t.key]} accounts (${pct.toFixed(1)}%)`}
                  data-testid={`ts-tier-bar-${t.key.toLowerCase()}`}
                >
                  {pct >= 8 && <span className="px-2">{data.distribution[t.key]}</span>}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
            {TIERS.map((t) => (
              <div key={t.key} className={`flex items-center gap-2 px-3 py-2 border ${t.border} ${t.bg} rounded-sm`} data-testid={`ts-tier-legend-${t.key.toLowerCase()}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${t.color}`} />
                <div className="flex-1">
                  <div className="font-semibold text-white">{t.key}</div>
                  <div className={`font-mono-plex ${t.text}`}>{data.distribution[t.key] || 0} accounts</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top risk accounts */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow text-ec-emerald">Top risk accounts</div>
              <h3 className="font-display text-xl font-bold mt-1 text-white">Highest fraud scores</h3>
            </div>
            <Link to="/admin/trust-safety/alerts"><Button size="sm" variant="ghost" className="rounded-sm text-zinc-300 hover:text-white gap-2">View all alerts <ArrowUpRight className="w-3 h-3" /></Button></Link>
          </div>
          {data.top_risk_accounts.length === 0 ? (
            <div className="py-10 text-center text-zinc-500" data-testid="ts-top-empty">
              <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-ec-emerald/60" />
              No medium-or-higher-risk accounts. Platform is healthy.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ts-top-table">
                <thead className="bg-white/5">
                  <tr className="text-left">
                    <th className="px-4 py-3 eyebrow text-zinc-500">Account</th>
                    <th className="px-4 py-3 eyebrow text-zinc-500">Role</th>
                    <th className="px-4 py-3 eyebrow text-zinc-500">Score</th>
                    <th className="px-4 py-3 eyebrow text-zinc-500">Tier</th>
                    <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_risk_accounts.map((a) => {
                    const tier = TIERS.find((t) => t.key === a.risk_level) || TIERS[0];
                    return (
                      <tr key={a.user_id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`ts-top-row-${a.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold">{a.name || "—"}</div>
                          <div className="text-xs text-zinc-500">{a.email}</div>
                          <div className="text-[10px] font-mono-plex text-zinc-600">{a.ethic_id}</div>
                        </td>
                        <td className="px-4 py-3"><Badge className="bg-white/10 border-white/10 rounded-sm">{a.role}</Badge></td>
                        <td className="px-4 py-3 font-mono-plex">
                          <div className="font-display text-2xl font-black text-white">{a.score}</div>
                          <div className="text-[10px] text-zinc-400">/ 100</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${tier.bg} ${tier.text} ${tier.border} border rounded-sm gap-1`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${tier.color}`} />
                            {a.risk_level}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/admin/trust-safety/account/${a.user_id}`}>
                            <Button size="sm" variant="outline" className="rounded-sm h-7 border-white/10 text-white hover:bg-white/10 gap-1" data-testid={`ts-investigate-${a.user_id}`}>
                              Investigate <ArrowUpRight className="w-3 h-3" />
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

function AlertKpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className={`p-5 bg-ec-ink border-l-2 ${accent}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="eyebrow text-zinc-500">{label}</div>
        <Icon className="w-3.5 h-3.5 text-zinc-400" />
      </div>
      <div className="font-display text-4xl font-black mt-2">{value.toLocaleString()}</div>
    </div>
  );
}
